// PrintBook.js - New dedicated page component
import { UseUserAuth } from "./context/UseAuthContext";
import PageNav from "./components/PageNav";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ExclamationCircleIcon, ArrowLeftIcon } from "@heroicons/react/20/solid";

const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'DC', name: 'District of Columbia' }
  ];
  
export default function PrintBook() {
  const { user } = UseUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get story data from navigation state
  const { storyId, storyTitle, orderNumber, createdDate } = location.state || {};
  
  const [step, setStep] = useState(1); // 1: Address, 2: Shipping Options
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [shippingData, setShippingData] = useState({
    street1: '',
    street2: '',
    city: '',
    state_code: '',
    postcode: '',
    country_code: 'US',
    phone_number: ''
  });
  
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const saleEndDate = new Date('2026-12-31T23:59:59');
  const currentDate = new Date();
  const isSaleActive = currentDate <= saleEndDate;
  const bookPrice = isSaleActive ? 20.00 : 25.00;
  const originalPrice = 25.00;
  // Redirect if no story data
  useEffect(() => {
    if (!storyId || !storyTitle) {
      navigate('/library');
    }
  }, [storyId, storyTitle, navigate]);

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/print/shipping-options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({
          storyId,
          storyTitle,
          shippingAddress: shippingData
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setShippingOptions(data.shippingOptions);
        setStep(2);
      } else {
        setError(data.error || 'Failed to get shipping options');
      }
    } catch (error) {
      console.error('Error getting shipping options:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShippingSelect = async () => {
    if (!selectedOption) return;
    
    setLoading(true);
    setError('');
    
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/print/calculate-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({
          storyId,
          storyTitle,
          shippingAddress: shippingData,
          shippingOption: selectedOption.name
        })
      });
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const data = await response.json();

      console.log('Response data type:', typeof data);
      console.log('Response data:', data);
      console.log('Is data a string?', typeof data === 'string');
      console.log('Does data start with https?', typeof data === 'string' && data.startsWith('https'));

      if (response.status === 303 ||response.ok) {
        // Redirect to Stripe checkout
        console.log('SUCCESS - About to redirect to:', data);
        console.log('Current window.location:', window.location.href);
        window.location.href = data;
      } else {
        console.log('FAILED - Error response:', response.status, data);
        setError(data.error || 'Failed to process order');
      }
    } catch (error) {
      console.error('Error processing order:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.emailVerified) {
    return (
      <div>
        <PageNav className="absolute inset-x-0 top-0 z-50" />
        <div className="flex justify-center bg-white px-6 py-10 lg:px-8">
          <div className="text-center">
            <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500" />
            <p className="mt-4 text-gray-600">
              Please verify your email address to print books.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageNav className="absolute inset-x-0 top-0 z-50" />
      
      <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:px-0">
        {/* Back button */}
        <button
          onClick={() => navigate('/library')}
          className="flex items-center text-indigo-600 hover:text-indigo-500 mb-8"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Library
        </button>

        {/* Story Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Print Your Story</h1>
          <div className="border-l-4 border-indigo-500 pl-4">
            <h2 className="text-lg font-semibold text-gray-800">{storyTitle}</h2>
            {orderNumber && <p className="text-sm text-gray-600">OrderNumber: {orderNumber}</p>}
            {createdDate && <p className="text-sm text-gray-600">Created: {createdDate}</p>}
          </div>
          <div className="mt-4 p-4 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Book Price:</strong> {isSaleActive ? (
  <>
    <span className="line-through text-gray-500">${originalPrice.toFixed(2)}</span>{' '}
    <span className="text-green-600 font-semibold">${bookPrice.toFixed(2)}</span>
  </>
) : (
  <span>${bookPrice.toFixed(2)}</span>
)} + shipping cost
            </p>
            <p className="text-xs text-blue-600 mt-1">
              High-quality paperback, full-color printing, professional binding
            </p>
            {isSaleActive && (
    <p className="text-xs text-green-700 mt-1 font-medium">
      🎉 20% Off Launch Special - Limited Time!
    </p>
  )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: Shipping Address */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Shipping Address</h3>
              
              <form onSubmit={handleAddressSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    value={shippingData.street1}
                    onChange={(e) => setShippingData({...shippingData, street1: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apartment, suite, etc. (optional)
                  </label>
                  <input
                    type="text"
                    value={shippingData.street2}
                    onChange={(e) => setShippingData({...shippingData, street2: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      value={shippingData.city}
                      onChange={(e) => setShippingData({...shippingData, city: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                    </label>
                    <select
                        value={shippingData.state_code}
                        onChange={(e) => setShippingData({...shippingData, state_code: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                    >
                        <option value="">Select State</option>
                        {US_STATES.map((state) => (
                        <option key={state.code} value={state.code}>
                            {state.name} ({state.code})
                        </option>
                        ))}
                    </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      value={shippingData.postcode}
                      onChange={(e) => setShippingData({...shippingData, postcode: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={shippingData.phone_number}
                      onChange={(e) => setShippingData({...shippingData, phone_number: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Getting Shipping Options...' : 'Continue'}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Shipping Options */}
          {step === 2 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Select Shipping Method</h3>
               {/** button
                  onClick={() => setStep(1)}
                  className="text-indigo-600 hover:text-indigo-500 text-sm"
                >
                  Edit Address
                </button>*/}
              </div> 

              <div className="space-y-4 mb-6">
                {shippingOptions.map((option, index) => (
                  <div 
                    key={index} 
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedOption?.id === option.id 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedOption(option)}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id={`shipping-${index}`}
                        name="shipping"
                        value={option.id}
                        checked={selectedOption?.id === option.id}
                        onChange={() => setSelectedOption(option)}
                        className="h-4 w-4 text-indigo-600"
                      />
                      <label htmlFor={`shipping-${index}`} className="ml-3 flex-1 cursor-pointer">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{option.name}</p>
                            <p className="text-sm text-gray-600">{option.delivery_time}</p>
                          </div>
                          <p className="font-semibold text-gray-900">${option.cost}</p>
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              {selectedOption && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-2">Order Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Book Price {isSaleActive && <span className="text-green-600 text-xs">(20% off!)</span>}</span>
                      <span>
          {isSaleActive ? (
            <>
              <span className="line-through text-gray-400 text-sm">${originalPrice.toFixed(2)}</span> ${bookPrice.toFixed(2)}
            </>
          ) : (
            `$${bookPrice.toFixed(2)}`
          )}
        </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shipping ({selectedOption.name})</span>
                      <span>${selectedOption.cost}</span>
                    </div>
                    <div className="border-t pt-1 flex justify-between font-semibold">
                      <span>Total</span>
                      <span>${(bookPrice + parseFloat(selectedOption.cost)).toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Taxes will be calculated at checkout
                    </p>
                  </div>
                </div>
              )}

              <button
                onClick={handleShippingSelect}
                disabled={!selectedOption || loading}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Continue to Payment'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}